import { Injectable, signal, untracked } from '@angular/core';
import { ControlService } from 'src/app/pages/learner/call/library/control.service';
import { RealtimeService } from 'src/app/pages/learner/call/library/realtime/realtime.service';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import { UserVideoService } from 'src/app/pages/learner/call/library/user-video.service';
import { DeviceSettingsService } from 'src/app/pages/learner/call/library/device-settings.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { AppConstants } from 'src/app/app-constants';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';

export interface ICallOverlayControlParams {
  moduleAssignmentUuid: string;
  callUuid: string;
  callType: string;
  practiceType?: string;
}

export interface ICallOverlayStartParams {
  controlParams: ICallOverlayControlParams;
  realtimeCallData: ICallRealtime;
  isGuided?: boolean;
}

export interface ICallOverlayServices {
  realtimeService: RealtimeService;
  controlService: ControlService;
  callStore: CallStoreService;
  userVideoService?: UserVideoService;
  deviceSettingsService?: DeviceSettingsService;
}

@Injectable()
export class CallOverlayService {
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly isCallActive = signal(false);

  private services: ICallOverlayServices | null = null;

  constructor(private readonly loggerService: LoggerService) {}

  init(services: ICallOverlayServices): void {
    this.services = services;
  }

  async startCall(params: ICallOverlayStartParams): Promise<void> {
    if (!this.services) {
      this.loggerService.error('[CALL_OVERLAY] init() must be called before startCall()');
      return;
    }

    const { realtimeService, controlService, callStore, userVideoService, deviceSettingsService } = this.services;
    const { controlParams, realtimeCallData, isGuided = false } = params;
    const {
      moduleAssignmentUuid,
      callUuid,
      callType,
      practiceType = PathwayConstants.PRACTICE_TYPE_PATHWAY,
    } = controlParams;

    if (!callUuid || !moduleAssignmentUuid) {
      this.loggerService.error('[CALL_OVERLAY] startCall: missing callUuid or moduleAssignmentUuid');
      return;
    }

    if (!realtimeCallData.conversationId || !realtimeCallData.conversationUrl) {
      this.loggerService.error('[CALL_OVERLAY] startCall: realtimeCallData is missing conversationId or conversationUrl');
      return;
    }

    this.isLoading.set(true);
    this.hasError.set(false);
    this.isCallActive.set(false);

    try {
      controlService.setQueryParams(moduleAssignmentUuid, callUuid, practiceType, callType, undefined);
      callStore.setCallUuid(callUuid);
      callStore.setCallType(callType);
      callStore.setPracticeCallUuid(realtimeCallData.practiceCallUuid);
      callStore.setCurrentView(AppConstants.VIEW_PRACTICE);
      callStore.setDisplayPostCall(false);
      callStore.setIsMicOn(untracked(() => callStore.getUserMicPreference()));

      await realtimeService.initializeFromStartConversationResponse(realtimeCallData, isGuided);
      realtimeService.conversationInitializedByPathwayFlow = true;
      callStore.setCurrentView(AppConstants.VIEW_PRACTICE);

      if (userVideoService) {
        userVideoService.setControlService(controlService);
        if (deviceSettingsService) {
          userVideoService.setDeviceSettingsService(deviceSettingsService);
        }
        // Preamble flow turns isVideoOn off — restore it before requesting camera
        if (!controlService.isVideoOn) {
          controlService.isVideoOn = true;
        }
        userVideoService.displayUserVideo();
      }

      this.isLoading.set(false);
      this.isCallActive.set(true);
    } catch (error) {
      this.loggerService.error('[CALL_OVERLAY] startCall failed', error);
      this.isLoading.set(false);
      this.hasError.set(true);
    }
  }

  destroy(): void {
    this.isLoading.set(false);
    this.hasError.set(false);
    this.isCallActive.set(false);
  }
}
